/** Membership still counts as subscribed (blocks new sale) until cancelled/expired or past end date.
 *  Previously imported TBC / undated placeholders do NOT block — rule applies to real dated memberships only.
 */
export function isMembershipUnexpired(m) {
  const status = String(m.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'expired') return false;

  // Legacy import / "dates to be confirmed" — not a live subscription period
  if (m.datesTbc || m.dates_tbc) return false;

  const expiry = String(m.expiry || m.expiryDate || m.expiry_date || '').slice(0, 10);
  const start = String(m.start || m.startDate || m.start_date || '').slice(0, 10);

  // No confirmed start/expiry — treat as placeholder, not blocking
  if (!expiry && !start) return false;

  if (expiry) {
    const today = new Date().toISOString().slice(0, 10);
    if (expiry < today) return false;
  }
  return true;
}

/** True if membership still blocks buying the same package again (active, not past expiry). */
export function isMembershipCurrentlyActive(m) {
  const status = String(m.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'expired') return false;
  if (m.frozen || status === 'inactive') return false;
  return isMembershipUnexpired(m);
}

function sameMemberRecord(member, m) {
  const memberId = member?.id;
  const gymId = String(member?.gymId || '').trim().toLowerCase();
  return (
    (memberId && m.memberId === memberId)
    || (gymId && gymId !== '—' && String(m.gymId || '').trim().toLowerCase() === gymId)
  );
}

/** Member has any package that has not expired yet — hide from subscription sales. */
export function memberHasUnexpiredSubscription(member, memberships) {
  if (!member) return false;
  return (memberships || []).some((m) => sameMemberRecord(member, m) && isMembershipUnexpired(m));
}

/** True if member already has this package via a current (non-expired) membership. */
export function memberHasPackage(member, memberships, packageName) {
  if (!member || !packageName) return false;
  const pkg = String(packageName).trim().toLowerCase();
  return (memberships || []).some((m) => {
    if (!sameMemberRecord(member, m)) return false;
    if (String(m.package || '').trim().toLowerCase() !== pkg) return false;
    return isMembershipCurrentlyActive(m);
  });
}

/** Active / non-expired packages already taken by this member. */
export function memberTakenPackages(member, memberships) {
  if (!member) return [];
  const names = new Set();
  (memberships || []).forEach((m) => {
    if (!sameMemberRecord(member, m)) return;
    if (!isMembershipCurrentlyActive(m)) return;
    const pkg = String(m.package || '').trim();
    if (pkg) names.add(pkg);
  });
  return [...names];
}

/** True if member has ever had any membership (registration fee already applied once). */
export function memberHasAnyPriorMembership(member, memberships) {
  if (!member?.id) return false;
  return (memberships || []).some((m) => m.memberId === member.id);
}
